import { Card, CardContent } from "../../../client/components/ui/card";
import { CardSectionHeader } from "../../../client/components/ui/card-section-header";
import { useDashboardData } from "../DashboardDataContext";
import { pnlColor } from "../chart/chartColors";

const COLOR_WIN = "hsl(var(--edgely-pnl-positive))";
const COLOR_LOSS = "hsl(var(--edgely-pnl-negative))";
const COLOR_MUTED = "hsl(var(--muted-foreground))";
const COLOR_WIN_BG = "hsl(var(--edgely-pnl-positive) / 0.12)";
const COLOR_LOSS_BG = "hsl(var(--edgely-pnl-negative) / 0.12)";

function EmptyState({ label }: { label: string }) {
  return (
    <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
      {label}
    </div>
  );
}

function fmtMoney(v: number): string {
  return `${v >= 0 ? "+" : ""}${v.toFixed(2)} $`;
}

function StreakTile({
  label,
  length,
  tradesLabel,
}: {
  label: string;
  length: number;
  tradesLabel: string;
}) {
  return (
    <div className="flex flex-col gap-1 rounded-lg bg-muted/40 p-3">
      <p className="text-[10px] font-semibold uppercase tracking-widest widget-label">{label}</p>
      <p className="font-mono text-xl font-bold">{length}</p>
      <p className="text-[10px] uppercase tracking-wide text-muted-foreground">{tradesLabel}</p>
    </div>
  );
}

function MgmtBar({
  label,
  ok,
  total,
}: {
  label: string;
  ok: number;
  total: number;
}) {
  const ratio = total > 0 ? (ok / total) * 100 : 0;
  return (
    <div className="flex flex-col gap-1.5 rounded-lg bg-muted/40 p-3">
      <div className="flex items-baseline justify-between">
        <p className="text-[10px] font-semibold uppercase tracking-widest widget-label">{label}</p>
        <span className="font-mono text-[11px] font-bold" style={{ color: total > 0 ? COLOR_WIN : COLOR_MUTED }}>
          {total > 0 ? `${ok}/${total}` : "—"}
        </span>
      </div>
      <div className="h-1.5 w-full overflow-hidden rounded-full bg-muted/30">
        <div
          className="h-1.5 rounded-full transition-all"
          style={{ width: `${Math.min(ratio, 100)}%`, backgroundColor: COLOR_WIN }}
        />
      </div>
    </div>
  );
}

export default function StreakAnalysisWidget() {
  const { analyticsData, isDark, t } = useDashboardData();

  if (!analyticsData || !analyticsData.streakData) {
    return (
      <Card className="h-full flex flex-col">
        <CardSectionHeader label={t.dashboard.widgets.streakAnalysis} isDark={isDark} compact />
        <CardContent className="flex-1 p-4">
          <EmptyState label={t.analytics.noDataAvailable} />
        </CardContent>
      </Card>
    );
  }

  const sd = analyticsData.streakData;
  const isWin = sd.currentStreak.type === "win";
  const currentColor = isWin ? COLOR_WIN : COLOR_LOSS;
  const currentBg = isWin ? COLOR_WIN_BG : COLOR_LOSS_BG;
  const currentLabel = isWin ? t.analytics.streakWins : t.analytics.streakLosses;

  return (
    <Card className="h-full flex flex-col">
      <CardSectionHeader label={t.dashboard.widgets.streakAnalysis} isDark={isDark} compact />
      <CardContent className="flex-1 flex flex-col justify-center gap-3 px-4 pb-4 pt-2">
        {/* Current streak */}
        <div
          className="flex items-baseline justify-between rounded-lg p-3"
          style={{ backgroundColor: currentBg }}
        >
          <div className="flex flex-col gap-0.5">
            <p className="text-[10px] font-semibold uppercase tracking-widest widget-label">
              {t.analytics.currentStreak}
            </p>
            <p className="font-mono text-lg font-bold" style={{ color: currentColor }}>
              {sd.currentStreak.length} {currentLabel}
            </p>
          </div>
          <p className="font-mono text-sm font-bold" style={{ color: pnlColor(sd.currentStreak.totalPnl) }}>
            {fmtMoney(sd.currentStreak.totalPnl)}
          </p>
        </div>

        {/* Longest streaks */}
        <div className="grid grid-cols-2 gap-3">
          <StreakTile
            label={t.analytics.longestWinStreak}
            length={sd.longestWin.length}
            tradesLabel={t.analytics.trades}
          />
          <StreakTile
            label={t.analytics.longestLossStreak}
            length={sd.longestLoss.length}
            tradesLabel={t.analytics.trades}
          />
        </div>

        {/* Risk management */}
        <div className="flex flex-col gap-2">
          <p className="text-[10px] font-semibold uppercase tracking-widest widget-label">
            {t.analytics.riskManagement}
          </p>
          <div className="grid grid-cols-2 gap-3">
            <MgmtBar
              label={t.analytics.drawdownMgmt}
              ok={sd.drawdownMgmtOk}
              total={sd.drawdownMgmtTotal}
            />
            <MgmtBar
              label={t.analytics.drawupMgmt}
              ok={sd.drawupMgmtOk}
              total={sd.drawupMgmtTotal}
            />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
